import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  Mail, 
  MessageSquare, 
  Share2, 
  FileText,
  GitBranch,
  Users,
  TrendingUp,
  Search,
  Globe,
  Code,
  Bug,
  TestTube,
  Lightbulb,
  Edit3,
  Palette,
  Briefcase,
  BarChart,
  Shield,
  Zap
} from 'lucide-react';

const AIApps = ({ onSelectApp, onClose, isFromWelcome = false }) => {
  const [hoveredApp, setHoveredApp] = useState(null);
  const navigate = useNavigate();

  const appCategories = [
    {
      name: 'Content Creation',
      apps: [
        {
          id: 'draft-composer',
          name: 'Draft Composer',
          icon: Mail,
          color: 'bg-blue-500',
          description: 'Multi-tone email & document writer',
          systemPrompt: `You are an expert writing assistant specializing in creating professional communications. 
When users provide a topic or request:
1. First ask about the desired tone (Professional, Friendly, Persuasive, Formal, Casual)
2. Ask about the target audience and key points to cover
3. Generate a well-structured draft with:
   - Clear subject/title
   - Engaging opening
   - Organized body content
   - Strong conclusion/call-to-action
4. Offer to adjust tone, length, or style based on feedback
5. Provide alternative versions if requested`
        },
        {
          id: 'response-generator',
          name: 'Response Generator',
          icon: MessageSquare,
          color: 'bg-green-500',
          description: 'Smart reply assistant with context awareness',
          systemPrompt: `You are a response generation expert. When users share a message they need to respond to:
1. Analyze the tone and context of the original message
2. Ask about the relationship (colleague, client, friend, etc.)
3. Generate 3 response options:
   - Brief and direct
   - Detailed and thorough
   - Diplomatic and balanced
4. Match the appropriate tone and formality level
5. Include relevant points that need addressing`
        },
        {
          id: 'social-media',
          name: 'Social Media Writer',
          icon: Share2,
          color: 'bg-purple-500',
          description: 'Platform-specific content creator',
          systemPrompt: `You are a social media content expert. When creating content:
1. Ask which platform (LinkedIn, Twitter/X, Instagram, etc.)
2. Understand the goal (engagement, education, promotion)
3. Create platform-optimized content:
   - LinkedIn: Professional, value-driven, 1300 chars max
   - Twitter/X: Concise, engaging, thread-friendly
   - Instagram: Visual descriptions, hashtags, emoji-friendly
4. Include relevant hashtags and CTAs
5. Suggest posting times and content variations`
        }
      ]
    },
    {
      name: 'Product Development',
      apps: [
        {
          id: 'prd-generator',
          name: 'PRD Generator',
          icon: FileText,
          color: 'bg-indigo-500',
          description: 'Creates comprehensive Product Requirements Documents',
          systemPrompt: `You are an expert product manager specializing in creating comprehensive PRDs. 
When the user provides a product idea or feature request:
1. Ask clarifying questions about target users, business goals, and constraints
2. Generate a structured PRD including:
   - Executive Summary
   - Problem Statement  
   - User Personas
   - Functional Requirements
   - Success Metrics
   - Technical Considerations
   - Timeline Estimates
3. Use clear, concise language appropriate for stakeholders
4. Include acceptance criteria for each requirement
5. Suggest MVP vs full feature set approaches`
        },
        {
          id: 'user-story',
          name: 'User Story Creator',
          icon: Users,
          color: 'bg-cyan-500',
          description: 'Generates detailed user stories',
          systemPrompt: `You are an agile development expert specializing in user stories. When creating stories:
1. Follow the format: As a [user type], I want [goal], so that [benefit]
2. Include comprehensive acceptance criteria
3. Add technical notes and dependencies
4. Estimate story points (S/M/L/XL)
5. Identify potential edge cases and risks
6. Group related stories into epics
7. Prioritize based on user value and effort`
        }
      ]
    },
    {
      name: 'Business Analysis',
      apps: [
        {
          id: 'competitor-analyzer',
          name: 'Competition Analyzer',
          icon: TrendingUp,
          color: 'bg-red-500',
          description: 'Analyzes competitor strategies and positioning',
          systemPrompt: `You are a strategic business analyst expert in competitive intelligence.
When analyzing competitors:
1. Research and compare key aspects:
   - Product features and pricing
   - Market positioning
   - Target audience
   - Strengths and weaknesses
   - Marketing strategies
2. Present findings in a structured format
3. Identify opportunities and threats
4. Suggest actionable recommendations
5. Use data-driven insights when possible
6. Create comparison matrices and SWOT analyses`
        },
        {
          id: 'market-research',
          name: 'Market Research',
          icon: BarChart,
          color: 'bg-orange-500',
          description: 'Trend analysis and opportunity identifier',
          systemPrompt: `You are a market research analyst. When conducting research:
1. Identify key market trends and drivers
2. Analyze target audience demographics and behaviors
3. Estimate market size and growth potential
4. Identify emerging opportunities and threats
5. Provide data-backed recommendations
6. Create executive summaries with key insights
7. Suggest research methodologies for validation`
        }
      ]
    },
    {
      name: 'Website & SEO',
      apps: [
        {
          id: 'website-auditor',
          name: 'Website Auditor',
          icon: Globe,
          color: 'bg-teal-500',
          description: 'Reviews design, copy, and SEO elements',
          systemPrompt: `You are a website optimization expert. When auditing websites:
1. Evaluate key areas:
   - Design and user experience
   - Content quality and messaging
   - SEO elements (meta, headers, keywords)
   - Page speed and performance
   - Mobile responsiveness
   - Conversion optimization
2. Provide specific, actionable recommendations
3. Prioritize fixes by impact and effort
4. Include before/after examples
5. Reference current best practices`
        },
        {
          id: 'seo-optimizer',
          name: 'SEO Optimizer',
          icon: Search,
          color: 'bg-yellow-500',
          description: 'Keyword research and content optimization',
          systemPrompt: `You are an SEO specialist. When optimizing content:
1. Conduct keyword research and analysis
2. Optimize on-page elements:
   - Title tags and meta descriptions
   - Header structure (H1-H6)
   - Content optimization and keyword density
   - Internal linking suggestions
   - Image alt text
3. Provide content gap analysis
4. Suggest schema markup implementation
5. Create SEO-friendly content outlines`
        }
      ]
    },
    {
      name: 'Technical Tools',
      apps: [
        {
          id: 'code-reviewer',
          name: 'Code Reviewer',
          icon: Code,
          color: 'bg-gray-600',
          description: 'Reviews code with feedback and suggestions',
          systemPrompt: `You are a senior software engineer specializing in code reviews. When reviewing code:
1. Analyze for:
   - Code quality and readability
   - Performance optimization opportunities
   - Security vulnerabilities
   - Best practices and design patterns
   - Error handling and edge cases
2. Provide constructive feedback with examples
3. Suggest refactoring approaches
4. Highlight what's done well
5. Prioritize issues by severity`
        },
        {
          id: 'bug-reporter',
          name: 'Bug Report Writer',
          icon: Bug,
          color: 'bg-pink-500',
          description: 'Structured bug documentation',
          systemPrompt: `You are a QA engineer expert in bug reporting. When documenting bugs:
1. Create comprehensive bug reports with:
   - Clear, descriptive title
   - Steps to reproduce
   - Expected vs actual behavior
   - Environment details
   - Screenshots/error logs
   - Severity and priority
2. Suggest potential root causes
3. Include workarounds if known
4. Link related issues
5. Follow standard bug report templates`
        }
      ]
    },
    {
      name: 'Creative Tools',
      apps: [
        {
          id: 'brainstorm-partner',
          name: 'Brainstorming Partner',
          icon: Lightbulb,
          color: 'bg-amber-500',
          description: 'Idea generation with creative techniques',
          systemPrompt: `You are a creative thinking facilitator. When brainstorming:
1. Use various creative techniques:
   - Mind mapping
   - SCAMPER method
   - Six Thinking Hats
   - Random word association
   - Reverse brainstorming
2. Build on ideas without judgment
3. Encourage wild and unconventional thinking
4. Organize ideas by themes
5. Help prioritize and refine top concepts`
        },
        {
          id: 'copywriting',
          name: 'Copywriting Assistant',
          icon: Edit3,
          color: 'bg-rose-500',
          description: 'Headlines, taglines, and marketing copy',
          systemPrompt: `You are a professional copywriter. When creating copy:
1. Understand the brand voice and target audience
2. Create compelling:
   - Headlines that grab attention
   - Taglines that stick
   - Body copy that converts
   - CTAs that drive action
3. Use proven copywriting formulas (AIDA, PAS, etc.)
4. A/B testing variations
5. Optimize for different channels and formats`
        }
      ]
    }
  ];

  const handleAppClick = (app) => {
    if (isFromWelcome) {
      // Navigate to new app conversation from welcome screen
      navigate('/apps/conversation/new', { 
        state: { 
          appId: app.id,
          appName: app.name,
          systemPrompt: app.systemPrompt,
          metadata: {
            ...app,
            icon: undefined // Remove icon to avoid serialization error
          }
        }
      });
    } else if (onSelectApp) {
      // Use callback if provided
      onSelectApp(app);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-500" />
              Specialized AI Assistants
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select an assistant pre-configured for your specific task
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {appCategories.map((category, categoryIndex) => (
            <div key={category.name} className={categoryIndex > 0 ? 'mt-8' : ''}>
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                {category.name}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.apps.map((app) => (
                  <button
                    key={app.id}
                    onClick={() => handleAppClick(app)}
                    onMouseEnter={() => setHoveredApp(app.id)}
                    onMouseLeave={() => setHoveredApp(null)}
                    className="group relative p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-lg transition-all duration-200 text-left"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`${app.color} p-2 rounded-lg text-white group-hover:scale-110 transition-transform`}>
                        <app.icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {app.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {app.description}
                        </p>
                      </div>
                    </div>
                    
                    {hoveredApp === app.id && (
                      <div className="absolute bottom-2 right-2 text-xs text-blue-500 dark:text-blue-400">
                        Click to start →
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Lightbulb className="w-4 h-4 text-yellow-500" />
            <span>Tip: Each assistant is pre-configured with specialized prompts for optimal results</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIApps;